/**
 * ExpenseTable Component
 * Data table for displaying expenses with sorting and filtering
 */

import React, { useState, useMemo } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/Badge';

export interface Expense {
  id: string;
  description: string;
  amount: number;
  currency: string;
  category: {
    id: string;
    name: string;
  };
  expense_date: string;
  status: 'draft' | 'submitted' | 'pending_approval' | 'approved' | 'rejected' | 'paid' | 'cancelled';
  merchant_name?: string;
  receipt_url?: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
}

export interface ExpenseTableProps {
  expenses: Expense[];
  loading?: boolean;
  initialized?: boolean;
  onExpenseClick?: (expense: Expense) => void;
  onStatusChange?: (expenseId: string, status: Expense['status']) => void;
  className?: string;
  showActions?: boolean;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
}

type SortField = keyof Expense;
type SortDirection = 'asc' | 'desc';

export function ExpenseTable({
  expenses,
  loading = false,
  initialized = false,
  onExpenseClick,
  onStatusChange,
  className,
  showActions = true,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
}: ExpenseTableProps) {
  const [sortField, setSortField] = useState<SortField>('expense_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedExpenses = useMemo(() => {
    return [...expenses].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Handle different field types
      switch (sortField) {
        case 'category':
          aValue = a.category.name;
          bValue = b.category.name;
          break;
        case 'user':
          aValue = a.user.name;
          bValue = b.user.name;
          break;
        case 'expense_date':
          aValue = new Date(a.expense_date);
          bValue = new Date(b.expense_date);
          break;
        default:
          aValue = a[sortField as keyof Expense];
          bValue = b[sortField as keyof Expense];
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1;

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [expenses, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange?.(expenses.map(expense => expense.id));
    } else {
      onSelectionChange?.([]);
    }
  };

  const handleSelectExpense = (expenseId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, expenseId]);
    } else {
      onSelectionChange?.(selectedIds.filter(id => id !== expenseId));
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <th className="px-6 py-3 text-left">
      <button
        onClick={() => handleSort(field)}
        className="group flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
      >
        <span>{children}</span>
        <div className="flex flex-col">
          <svg
            className={cn(
              'w-3 h-3 transition-colors',
              sortField === field && sortDirection === 'asc'
                ? 'text-primary-600'
                : 'text-gray-300 group-hover:text-gray-400'
            )}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          <svg
            className={cn(
              'w-3 h-3 -mt-1 transition-colors',
              sortField === field && sortDirection === 'desc'
                ? 'text-primary-600'
                : 'text-gray-300 group-hover:text-gray-400'
            )}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>
    </th>
  );

  if (loading) {
    return (
      <div className={cn('bg-white shadow rounded-lg', className)}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" aria-hidden="true" />
          <span className="text-sm text-gray-600">Loading expenses…</span>
        </div>
        <div className="animate-pulse">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-50 border-t border-gray-200"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-white shadow rounded-lg overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {selectable && (
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === expenses.length && expenses.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </th>
              )}
              <SortableHeader field="description">Description</SortableHeader>
              <SortableHeader field="amount">Amount</SortableHeader>
              <SortableHeader field="category">Category</SortableHeader>
              <SortableHeader field="expense_date">Date</SortableHeader>
              <SortableHeader field="status">Status</SortableHeader>
              <SortableHeader field="user">Submitted By</SortableHeader>
              {showActions && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedExpenses.map((expense) => (
              <tr
                key={expense.id}
                className={cn(
                  'hover:bg-gray-50 transition-colors',
                  onExpenseClick && 'cursor-pointer'
                )}
                onClick={() => onExpenseClick?.(expense)}
              >
                {selectable && (
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(expense.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectExpense(expense.id, e.target.checked);
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {expense.description}
                      </div>
                      {expense.merchant_name && (
                        <div className="text-sm text-gray-500">
                          {expense.merchant_name}
                        </div>
                      )}
                    </div>
                    {expense.receipt_url && (
                      <div className="ml-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(expense.amount, expense.currency)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {expense.category.name}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(new Date(expense.expense_date))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={expense.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {expense.user.name}
                </td>
                {showActions && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onExpenseClick?.(expense);
                        }}
                      >
                        View
                      </Button>
                      {expense.status === 'pending_approval' && onStatusChange && (
                        <>
                          <Button
                            variant="success"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onStatusChange(expense.id, 'approved');
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onStatusChange(expense.id, 'rejected');
                            }}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {initialized && expenses.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new expense.</p>
        </div>
      )}
    </div>
  );
}
